var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from "react";
// mui
import { Box, Popover, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterListIcon from "@mui/icons-material/FilterList";
// hook form
import { useFormContext, useWatch } from "react-hook-form";
// constants / types
import { FilterType } from "./types";
//components
import { ChipV2 as Chip, Text } from "../../Atoms";
import { ActiveFilter, FilteringOptions } from "./components";
// utilities
import { minMaxMessaging, selectListMessaging } from "../../../helpers";
import { COLORS } from "../../../constants";
// hardcode the min and maxname used
var maxName = "lte";
var minName = "gte";
var FilterSelectionFlow = function (_a, ref) {
    var _b = _a.skipMessaging, skipMessaging = _b === void 0 ? false : _b, withinForm = _a.withinForm, chipProps = _a.chipProps, sx = _a.sx, onClear = _a.onClear, _c = _a.clearable, clearable = _c === void 0 ? true : _c, onClose = _a.onClose, onSubmit = _a.onSubmit, values = _a.values, _d = _a.chipStartWord, chipStartWord = _d === void 0 ? "Owns" : _d, _e = _a.chipEndWord, chipEndWord = _e === void 0 ? "share" : _e, chipLabel = _a.chipLabel, filterTypeOptions = _a.filterTypeOptions, isPercent = _a.isPercent, _f = _a.testid, testid = _f === void 0 ? "filterFlowChip" : _f;
    var chipRef = useRef(null);
    // allow parent ref to run triggerPopover
    useImperativeHandle(ref, function () { return ({ triggerPopover: triggerPopover }); });
    // for popover
    var actionRef = useRef(null);
    var _g = __read(useState(null), 2), anchorEl = _g[0], setAnchorEl = _g[1];
    // if this is used within a form
    var outerForm = useFormContext();
    // values retrieved from provided field names
    var fieldData = withinForm
        ? useWatch({ name: withinForm.fieldName })
        : undefined;
    var fieldData2 = (withinForm === null || withinForm === void 0 ? void 0 : withinForm.fieldName2)
        ? useWatch({ name: withinForm.fieldName2 })
        : undefined;
    var _h = __read(useState(null), 2), selectedFilter = _h[0], setFilter = _h[1];
    // values currently being used outside this popover
    var previousValues = values ||
        ((withinForm === null || withinForm === void 0 ? void 0 : withinForm.fieldName2) && fieldData
            ? { field1: fieldData, field2: fieldData2 }
            : fieldData);
    // determine current filter based on type
    var findFilter = function (typeArr) {
        return filterTypeOptions.find(function (o) { return typeArr.includes(o.type); });
    };
    var singleOption = filterTypeOptions.length === 1;
    // set active filter if one exists based on current values
    useEffect(function () {
        var _a;
        if (!anchorEl) {
            (_a = chipRef === null || chipRef === void 0 ? void 0 : chipRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        }
        if (anchorEl && !!previousValues && filterTypeOptions.length > 1) {
            var keys = Object.keys(previousValues);
            setFilter(findFilter(keys) || null);
        }
        else if (filterTypeOptions.length === 1) {
            setFilter(filterTypeOptions[0]);
        }
    }, [anchorEl]);
    // blur on clear
    useEffect(function () {
        var _a;
        if (!anchorEl && !fieldData) {
            (_a = chipRef === null || chipRef === void 0 ? void 0 : chipRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        }
    }, [fieldData]);
    // submit activeFilter
    var handleSubmit = function (data) {
        // if in a form set form values
        if (withinForm) {
            switch (selectedFilter === null || selectedFilter === void 0 ? void 0 : selectedFilter.type) {
                case FilterType.Range:
                    outerForm.setValue(withinForm.fieldName, {
                        range: {
                            gte: (data === null || data === void 0 ? void 0 : data[minName]) || undefined,
                            lte: (data === null || data === void 0 ? void 0 : data[maxName]) || undefined,
                        },
                    }, { shouldDirty: true });
                    break;
                case FilterType.Values:
                    outerForm.setValue(withinForm.fieldName, { values: data }, { shouldDirty: true });
                    break;
                case FilterType.Location:
                    outerForm.setValue(withinForm.fieldName, { values: data.state }, { shouldDirty: true });
                    outerForm.setValue(withinForm.fieldName2, data.city ? { values: data.city } : undefined, { shouldDirty: true });
                    break;
                case FilterType.Date:
                    outerForm.setValue(withinForm.fieldName, { values: data }, { shouldDirty: true });
                    break;
                case FilterType.Radio:
                    outerForm.setValue(withinForm.fieldName, { radio: data.value }, { shouldDirty: true });
                    break;
                /* istanbul ignore next */
                default:
                    break;
            }
            // if not in a form return data to consuming app
        }
        else {
            /* istanbul ignore else */
            if (onSubmit) {
                onSubmit(data, selectedFilter === null || selectedFilter === void 0 ? void 0 : selectedFilter.type);
            }
        }
        closePopover();
    };
    /**
     * open popver when chips clicked
     */
    var handleChipClick = useCallback(function (e) {
        setFilter(null);
        setAnchorEl(e.currentTarget);
    }, []);
    /**
     * Similar to handleChipClick, but triggered from the parent component to automate the selection
     */
    var triggerPopover = function () {
        setFilter(null);
        setAnchorEl(chipRef.current);
    };
    /**
     * clear whenever delete icon is clicked
     */
    var handleChipDeleteClick = useCallback(function () {
        clear();
    }, []);
    /**
     * reset form
     * call onClear callback
     * clear outside form values if exist
     * close popover
     */
    var clear = useCallback(function () {
        if (onClear) {
            onClear();
        }
        if (withinForm) {
            outerForm.setValue(withinForm.fieldName, undefined, {
                shouldDirty: true,
            });
            if (withinForm.fieldName2) {
                outerForm.setValue(withinForm.fieldName2, undefined, {
                    shouldDirty: true,
                });
            }
        }
        closePopover();
    }, [outerForm]);
    /**
     * close popover and trigger a close callback if one exists
     */
    var closePopover = useCallback(function () {
        setAnchorEl(null);
        if (onClose) {
            onClose();
        }
    }, []);
    /**
     * when selecting a filter type set the type to display it
     */
    var selectOptionType = function (option) {
        if (option.type === FilterType.Unselected) {
            clear();
        }
        else if (option.type === FilterType.None) {
            var range = {
                gte: 0,
                lte: 0,
            };
            if (!!withinForm) {
                outerForm.setValue(withinForm.fieldName, {
                    range: range,
                }, {
                    shouldDirty: true,
                });
            }
            if (onSubmit) {
                onSubmit(range);
            }
            closePopover();
        }
        else {
            setFilter(option);
        }
    };
    // if a value is currently set so we can show clear
    var hasValue = !!previousValues;
    /**
     * get messaging based on current selection values
     */
    var getMessaging = function () {
        var _a, _b, _c, _d, _e, _f;
        if (!!previousValues) {
            if (!!previousValues.range) {
                return minMaxMessaging((_a = previousValues.range.gte) === null || _a === void 0 ? void 0 : _a.toString(), (_b = previousValues.range.lte) === null || _b === void 0 ? void 0 : _b.toString(), chipStartWord, chipEndWord, isPercent);
            }
            if (!!previousValues.values) {
                var filter = findFilter([FilterType.Values]);
                /* istanbul ignore else */
                if ((filter === null || filter === void 0 ? void 0 : filter.type) === FilterType.Values) {
                    return selectListMessaging(previousValues.values, filter.options.options || [], chipStartWord, "".concat(chipEndWord, "s"));
                }
            }
            /* istanbul ignore else */
            if (!!((_d = (_c = previousValues.field1) === null || _c === void 0 ? void 0 : _c.values) === null || _d === void 0 ? void 0 : _d[0])) {
                return "".concat(chipStartWord, " ").concat(((_f = (_e = previousValues === null || previousValues === void 0 ? void 0 : previousValues.field2) === null || _e === void 0 ? void 0 : _e.values) === null || _f === void 0 ? void 0 : _f[0]) || previousValues.field1.values[0], " ").concat(chipEndWord).trim();
            }
        }
        return null;
    };
    var goBack = useCallback(function () {
        setFilter(null);
    }, []);
    // display label for chip
    var messaging = useMemo(function () { return getMessaging(); }, [previousValues, filterTypeOptions]);
    // add check for unselected
    var activeFilterType = useMemo(function () {
        var _a, _b, _c, _d, _e;
        if (!!previousValues) {
            // edge case check for if double 0 is selected, to include None which is equivalent to
            // range {min:0,max:0}
            var keys = Object.keys(previousValues);
            if (((_b = (_a = previousValues === null || previousValues === void 0 ? void 0 : previousValues.range) === null || _a === void 0 ? void 0 : _a[minName]) === null || _b === void 0 ? void 0 : _b.toString()) === "0" &&
                ((_d = (_c = previousValues === null || previousValues === void 0 ? void 0 : previousValues.range) === null || _c === void 0 ? void 0 : _c[maxName]) === null || _d === void 0 ? void 0 : _d.toString()) === "0") {
                keys.push(FilterType.None);
            }
            return ((_e = findFilter(keys)) === null || _e === void 0 ? void 0 : _e.type) || FilterType.Unselected;
        }
        return FilterType.Unselected;
    }, [previousValues]);
    return (_jsxs(Box, __assign({ sx: sx }, { children: [_jsx(Chip, __assign({}, chipProps, { variant: "secondary", "data-testid": testid, selected: !!hasValue || !!anchorEl, ref: chipRef, icon: _jsx(FilterListIcon, {}), onClick: handleChipClick, onDelete: hasValue && clearable ? handleChipDeleteClick : undefined, label: skipMessaging ? chipLabel : messaging || chipLabel })), _jsx(Popover, __assign({ id: "filterflow-form-popover", "data-testid": "filterflow-form-popover", open: !!anchorEl, action: actionRef, disableScrollLock: true, PaperProps: {
                    sx: {
                        borderRadius: 2,
                        width: "100%",
                        maxWidth: 380,
                        border: "1px solid ".concat(COLORS.SECONDARY_HOVER),
                    },
                }, anchorEl: anchorEl, onClose: closePopover, anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                }, transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                } }, { children: _jsx(Box, __assign({ p: 4 }, { children: !selectedFilter ? (_jsx(FilteringOptions, { selectOption: selectOptionType, options: filterTypeOptions, currentOption: activeFilterType })) : (_jsxs(Stack, { children: [!singleOption && (_jsxs(Stack, __assign({ direction: "row", alignItems: "center", mb: 7, "data-testid": "goback-filter", sx: { cursor: "pointer" }, onClick: goBack }, { children: [_jsx(ArrowBackIcon, {}), _jsx(Text, __assign({ variant: "body2", ml: 2 }, { children: selectedFilter.label }))] }))), _jsx(Box, __assign({ pl: singleOption ? 0 : 7 }, { children: _jsx(ActiveFilter, { selectedFilter: selectedFilter, onClose: closePopover, onClear: clear, onSubmit: handleSubmit, isPercent: isPercent, values: previousValues, actionRef: actionRef }) }))] })) })) }))] })));
};
export default forwardRef(FilterSelectionFlow);
